import { z } from 'zod';
import { COMPANY_SIZES } from '../types';

// Login Schema
export const loginSchema = z.object({
  email: z.string().min(1, 'Email é obrigatório').email('Email deve ter um formato válido'),
  password: z
    .string()
    .min(1, 'Senha é obrigatória')
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
});

// Password Reset Schema
export const passwordResetSchema = z.object({
  email: z.string().min(1, 'Email é obrigatório').email('Email deve ter um formato válido')
});

// Password Creation Schema
export const passwordCreationSchema = z
  .object({
    name: z.string().min(1, 'Nome é obrigatório').min(2, 'Nome deve ter pelo menos 2 caracteres'),
    password: z
      .string()
      .min(1, 'Senha é obrigatória')
      .min(6, 'Senha deve ter pelo menos 6 caracteres'),
    passwordConfirm: z.string().min(1, 'Confirmação de senha é obrigatória')
  })
  .refine(data => data.password === data.passwordConfirm, {
    message: 'Senhas não conferem',
    path: ['passwordConfirm']
  });

// Paciente Schema
export const pacienteSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório').min(2, 'Nome deve ter pelo menos 2 caracteres'),
  cpf: z
    .string()
    .min(1, 'CPF é obrigatório')
    .refine(val => val.replace(/\D/g, '').length === 11, {
      message: 'CPF deve ter 11 dígitos'
    }),
  data_nasc: z.string().min(1, 'Data de nascimento é obrigatória'),
  celular: z
    .string()
    .min(1, 'Celular é obrigatório')
    .refine(val => val.replace(/\D/g, '').length >= 10, {
      message: 'Celular deve ter pelo menos 10 dígitos'
    }),
  email: z
    .string()
    .optional()
    .refine(val => !val || z.string().email().safeParse(val).success, {
      message: 'Email deve ter um formato válido'
    }),
  rg: z.string().optional(),
  convenio: z.string().optional(),
  profissao: z.string().optional(),
  estado_civil: z.string().optional(),
  telefone: z.string().optional(),
  celular_ii: z.string().optional(),
  cep: z.string().optional(),
  logradouro: z.string().optional(),
  numero: z.string().optional(),
  complemento: z.string().optional(),
  bairro: z.string().optional(),
  municipio: z.string().optional(),
  uf: z.string().optional(),
  obs: z.string().optional()
});

// Consulta Schema
export const consultaSchema = z.object({
  paciente_id: z.string().min(1, 'Paciente é obrigatório'),
  queixa: z.string().min(1, 'Queixa principal é obrigatória'),
  cirurgia_oftalmologica: z.boolean().optional(),
  dm: z.boolean().optional(),
  has: z.boolean().optional(),
  glaucoma_familia: z.boolean().optional(),
  refracao: z.enum(['dinamica', 'estatica']).optional(),
  od_esferico: z.string().optional(),
  od_cilindrico: z.string().optional(),
  od_eixo: z.string().optional(),
  od_av: z.string().optional(),
  oe_esferico: z.string().optional(),
  oe_cilindrico: z.string().optional(),
  oe_eixo: z.string().optional(),
  oe_av: z.string().optional(),
  adicao: z.string().optional(),
  biomicroscopia: z.string().optional(),
  fundo_de_olho: z.string().optional(),
  pio: z.string().optional(),
  conduta: z.string().optional()
});

// Organization Schema
export const organizationSchema = z.object({
  name: z
    .string()
    .min(1, 'Nome da organização é obrigatório')
    .min(2, 'Nome deve ter pelo menos 2 caracteres'),
  slug: z
    .string()
    .min(1, 'Slug é obrigatório')
    .regex(/^[a-z0-9-]+$/, 'Slug deve conter apenas letras minúsculas, números e hífens'),
  slogan: z.string().optional(),
  inviteEmail: z
    .string()
    .optional()
    .refine(val => !val || z.string().email().safeParse(val).success, {
      message: 'Email deve ter um formato válido'
    })
});

// Organization Settings Schema
export const organizationSettingsSchema = z.object({
  name: z
    .string()
    .min(1, 'Nome da organização é obrigatório')
    .min(2, 'Nome deve ter pelo menos 2 caracteres'),
  slogan: z.string().optional()
});

// Organization Setup Schema
export const organizationSetupSchema = z.object({
  organizationName: z
    .string()
    .min(1, 'Nome da organização é obrigatório')
    .min(2, 'Nome da organização deve ter pelo menos 2 caracteres'),
  organizationSlogan: z.string().optional(),
  companySize: z.enum(COMPANY_SIZES, {
    required_error: 'Tamanho da empresa é obrigatório'
  })
});

// Type exports
export type LoginFormData = z.infer<typeof loginSchema>;
export type PasswordResetFormData = z.infer<typeof passwordResetSchema>;
export type PasswordCreationFormData = z.infer<typeof passwordCreationSchema>;
export type PacienteFormData = z.infer<typeof pacienteSchema>;
export type ConsultaFormData = z.infer<typeof consultaSchema>;
export type OrganizationFormData = z.infer<typeof organizationSchema>;
export type OrganizationSettingsFormData = z.infer<typeof organizationSettingsSchema>;
export type OrganizationSetupFormData = z.infer<typeof organizationSetupSchema>;
